import { CheckCircle, AlertTriangle, Circle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type ItemStatus = 'pending' | 'good' | 'issues';

interface ProgressItem {
  id: string;
  label: string;
  status: ItemStatus;
}

interface InspectionProgressBarProps {
  items: ProgressItem[];
  currentIndex: number;
  onItemTap: (index: number) => void;
}

export function InspectionProgressBar({ items, currentIndex, onItemTap }: InspectionProgressBarProps) {
  const { t } = useTranslation('inspection');
  if (items.length === 0) return null;

  const completed = items.filter(i => i.status !== 'pending').length;

  return (
    <div className="bg-surface border border-line rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-theme-secondary">
          {t('progress.ofTotal', { current: currentIndex + 1, total: items.length })}
        </span>
        <span className="text-xs font-medium text-theme-secondary">
          {t('progress.inspected', { completed, total: items.length })}
        </span>
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide">
        {items.map((item, index) => {
          const isCurrent = index === currentIndex;
          let bgColor = 'bg-gray-200'; // theme-allow: pending progress-fill (status/data color)
          if (item.status === 'good') bgColor = 'bg-emerald-400';
          else if (item.status === 'issues') bgColor = 'bg-amber-400';

          return (
            <button
              key={item.id}
              onClick={() => onItemTap(index)}
              className={`flex-shrink-0 h-2 rounded-full transition-all ${bgColor} ${
                isCurrent ? 'ring-2 ring-blue-500 ring-offset-1 min-w-[20px]' : 'min-w-[12px]'
              }`}
              style={{ flex: isCurrent ? '2 1 0%' : '1 1 0%' }}
              title={item.label}
            />
          );
        })}
      </div>
    </div>
  );
}

interface InspectionQuickNavProps {
  items: ProgressItem[];
  currentIndex: number;
  onSelect: (index: number) => void;
  isOpen: boolean;
  onClose: () => void;
  title: string;
}

export function InspectionQuickNav({ items, currentIndex, onSelect, isOpen, onClose, title }: InspectionQuickNavProps) {
  const { t } = useTranslation('inspection');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-surface w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[70vh] flex flex-col animate-slide-up">
        <div className="flex items-center justify-between p-4 border-b border-line">
          <h3 className="font-semibold text-theme-primary">{title}</h3>
          <button onClick={onClose} className="text-theme-secondary hover:text-theme-primary text-sm font-medium">
            {t('progress.done')}
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-2">
          {items.map((item, index) => {
            const isCurrent = index === currentIndex;
            return (
              <button
                key={item.id}
                onClick={() => {
                  onSelect(index);
                  onClose();
                }}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg text-left transition-colors ${
                  isCurrent ? 'bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30' : 'hover:bg-subtle'
                }`}
              >
                {item.status === 'good' ? (
                  <CheckCircle size={18} className="text-emerald-500 flex-shrink-0" />
                ) : item.status === 'issues' ? (
                  <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />
                ) : (
                  <Circle size={18} className="text-theme-secondary flex-shrink-0" />
                )}
                <span className={`text-sm ${isCurrent ? 'font-semibold text-blue-700 dark:text-blue-300' : 'text-theme-primary'}`}>
                  {item.label}
                </span>
                {isCurrent && (
                  <span className="ml-auto text-xs bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">
                    {t('progress.current')}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
